/**
 * DOM-based HTML diff: compare original and result HTML and mark changed blocks
 * with .ai-added only (single highlight style for all AI changes).
 * Handles Hebrew/RTL text, React NodeView wrappers, empty blocks, and lists.
 */

const AI_DIFF_HIGHLIGHT_CLASS = "ai-added";

// NOTE: Don't treat `td` / `th` as standalone blocks.
// If we do, we can output orphaned <td>/<th> nodes without their <table>/<tr> wrappers,
// which breaks table structure after AI diffing.
const BLOCK_SELECTOR =
  "p, h1, h2, h3, h4, h5, h6, div, li, blockquote, pre, table, ol, ul";

function stripPreviousAiDiffMarkup(html) {
  if (typeof html !== "string" || !html.trim()) return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const body = doc.body || doc.documentElement;

    // Unwrap any previous inline diff spans so they don't affect matching.
    body.querySelectorAll("span.ai-inline").forEach((span) => {
      const parent = span.parentNode;
      if (!parent) return;
      while (span.firstChild) parent.insertBefore(span.firstChild, span);
      parent.removeChild(span);
    });

    // Remove ai-* classes from block elements (older diff strategy).
    body
      .querySelectorAll(".ai-added, .ai-modified, .ai-removed")
      .forEach((el) => {
        const cls = String(el.getAttribute("class") || "");
        if (!cls) return;
        const next = cls
          .split(/\s+/)
          .filter(Boolean)
          .filter((c) => c !== "ai-added" && c !== "ai-modified" && c !== "ai-removed")
          .join(" ");
        if (next) el.setAttribute("class", next);
        else el.removeAttribute("class");
      });

    return body.innerHTML || html;
  } catch (_) {
    // Best-effort regex fallback.
    return String(html)
      .replace(/<span[^>]*class=["'][^"']*\bai-inline\b[^"']*["'][^>]*>/gi, "")
      .replace(/<\/span>/gi, "")
      .replace(
        /\b(ai-added|ai-modified|ai-removed)\b/g,
        "",
      )
      .replace(/\sclass=["']\s*["']/gi, "");
  }
}

/**
 * Extract text content from HTML, removing React NodeView wrappers and other structural elements.
 * This ensures consistent text extraction regardless of wrapper structure differences.
 * @param {string} html - HTML string to extract text from
 * @returns {string} - Normalized text content
 */
function extractTextContent(html) {
  if (typeof html !== "string" || !html.trim()) return "";
  
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const body = doc.body || doc.documentElement;
    
    // Remove React NodeView wrapper elements before extracting text
    // Also remove any nested divs with these classes/attributes
    const wrappers = body.querySelectorAll(
      '[data-node-view-wrapper], [data-node-view-content], .react-renderer, [data-node-view-content-react]'
    );
    wrappers.forEach((wrapper) => {
      // Unwrap the wrapper, keeping its content
      const parent = wrapper.parentNode;
      if (parent) {
        while (wrapper.firstChild) {
          parent.insertBefore(wrapper.firstChild, wrapper);
        }
        parent.removeChild(wrapper);
      }
    });
    
    // Remove script and style tags
    const scripts = body.querySelectorAll("script, style");
    scripts.forEach((el) => el.remove());
    
    // Extract text content from cleaned DOM
    const text = body.textContent || body.innerText || "";
    return text;
  } catch (_) {
    // Fallback: regex-based extraction
    // Remove React wrapper patterns first
    let cleaned = html
      .replace(/<div[^>]*(?:data-node-view-wrapper|data-node-view-content|class="[^"]*react-renderer[^"]*")[^>]*>/gi, "")
      .replace(/<\/div>/gi, "")
      .replace(/<[^>]+>/g, " ") // Remove HTML tags
      .replace(/&nbsp;/gi, " ") // Replace &nbsp; with space
      .replace(/&[a-z0-9#]+;/gi, " ") // Replace HTML entities
      .replace(/\s+/g, " ") // Collapse whitespace
      .trim();
    return cleaned;
  }
}

/**
 * Normalize text for comparison: normalize whitespace while preserving content.
 * This ensures Hebrew and English text are compared correctly regardless of formatting differences.
 * Handles RTL text properly by normalizing whitespace but preserving character order.
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
function normalizeTextForComparison(text) {
  if (typeof text !== "string") return "";
  
  return text
    .normalize("NFKC")
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "") // strip bidi control chars
    .replace(/[\s\u00A0\u2000-\u200B\u2028\u2029\uFEFF]+/g, " ") // Normalize all whitespace (including &nbsp;, zero-width spaces, etc.)
    .trim();
}

/**
 * Aggressively normalize text for matching: removes ALL punctuation to handle cases
 * where AI adds commas, periods, or other punctuation but content is otherwise identical.
 * This is used specifically for matching blocks, not for display.
 * Handles all types of hyphens including Hebrew maqaf (־).
 * Treats hyphens and spaces as equivalent, so "ה CT" matches "ה-CT" and "ה- CT" -> all become "הCT".
 * @param {string} text - Text to normalize
 * @returns {string} - Aggressively normalized text (no punctuation, no spaces/hyphens between words)
 */
function normalizeTextForMatching(text) {
  if (typeof text !== "string") return "";
  
  // Remove RTL/LTR control chars and isolates that can differ between sources
  // and cause false positives for Hebrew content.
  // LRM/RLM, embeddings/overrides, isolates, etc.
  let normalized = text.normalize("NFKC").replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "");

  // First, convert all hyphens/dashes to spaces (treat them as equivalent to spaces)
  // This ensures "ה CT" and "ה-CT" both become "ה CT" before normalization
  normalized = normalized.replace(/[\u002D\u2013\u2014\u2011\u05BE]/g, " "); // Convert hyphens/dashes to spaces
  
  // Normalize all whitespace (including all Unicode whitespace variants)
  // This collapses multiple spaces/hyphens into single spaces
  normalized = normalized.replace(/[\s\u00A0\u2000-\u200B\u2028\u2029\uFEFF]+/g, " ");

  // Remove Hebrew diacritics (niqqud/cantillation) – these are often omitted inconsistently
  // and should not cause diff highlights.
  normalized = normalized.replace(/[\u0591-\u05C7]/g, "");

  // English/Latin matching should be case-insensitive.
  normalized = normalized.toLowerCase();
  
  // Remove punctuation (Latin + Hebrew quotes/geresh + common brackets/quotes)
  normalized = normalized.replace(/[.,;:!?\u05F3\u05F4"'“”‘’(){}[\]<>]/g, "");
  
  // Finally, remove all remaining whitespace to create a continuous string
  // This ensures "ה CT" and "ה-CT" both become "הCT"
  normalized = normalized.replace(/[\s\u00A0\u2000-\u200B\u2028\u2029\uFEFF]+/g, "");
  
  return normalized.trim();
}

/**
 * Check if a block is truly empty (only whitespace, &nbsp;, or structural elements).
 * @param {string} html - HTML content of the block
 * @param {string} textContent - Extracted text content
 * @returns {boolean}
 */
function isEmptyBlock(html, textContent) {
  if (!textContent || textContent.trim() === "") return true;
  
  // Check if HTML only contains whitespace/&nbsp; after removing tags
  const textOnly = html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&[a-z0-9#]+;/gi, " ")
    .replace(/[\s\u00A0\u2000-\u200B\u2028\u2029\uFEFF]+/g, "")
    .trim();
  
  return textOnly.length === 0;
}

/**
 * Extract list item text content from list HTML, ignoring structural differences.
 * Handles cases where list items may have <p> wrappers or not.
 * @param {string} listHtml - HTML content of a list (<ol> or <ul>)
 * @returns {string} - Normalized text content of all list items joined by "|"
 */
function extractListTextContent(listHtml) {
  if (!listHtml || typeof listHtml !== "string") return "";
  
  try {
    const doc = new DOMParser().parseFromString(listHtml, "text/html");
    const listItems = doc.querySelectorAll("li");
    const texts = Array.from(listItems).map((li) => {
      const text = extractTextContent(li.innerHTML);
      return normalizeTextForComparison(text);
    });
    return texts.join("|"); // Use separator to distinguish items
  } catch (_) {
    return normalizeTextForComparison(
      listHtml.replace(/<[^>]+>/g, " ").replace(/&nbsp;/gi, " ")
    );
  }
}

/**
 * Unwrap a single top-level div so we diff inner blocks.
 * @param {string} html
 * @returns {string}
 */
function unwrapSingleRootDiv(html) {
  if (typeof html !== "string" || !html.trim()) return html;
  const trimmed = html.trim();
  const divMatch = trimmed.match(/^<div[^>]*>([\s\S]*)<\/div>$/i);
  if (!divMatch) return html;
  const inner = divMatch[1].trim();
  if (!inner) return html;
  const onlyOneRoot =
    !inner.includes("</p>") &&
    !inner.includes("</h1>") &&
    !inner.includes("</h2>") &&
    !inner.includes("</h3>") &&
    !inner.includes("</h4>") &&
    !inner.includes("</h5>") &&
    !inner.includes("</h6>") &&
    !inner.includes("</div>") &&
    !inner.includes("</li>") &&
    !inner.includes("</table>") &&
    !inner.includes("</ol>") &&
    !inner.includes("</ul>") &&
    !inner.includes("</blockquote>");
  if (onlyOneRoot) return html;
  return inner;
}

/**
 * Check if a node has a block ancestor in the provided node list.
 * Used to filter out nested blocks (e.g., <p> inside <li> inside <ol>).
 */
function hasBlockAncestorInList(node, nodeList) {
  let parent = node.parentElement;
  while (parent && parent.id !== "html-diff-root") {
    if (nodeList.includes(parent)) return true;
    parent = parent.parentElement;
  }
  return false;
}

/**
 * Parse HTML into a list of block elements (tag + innerHTML + textContent).
 * Uses a temporary DOM; falls back to empty array on parse error.
 * Filters for top-level blocks only to avoid nested duplicates.
 * Extracts text content for comparison (ignoring React wrapper structures).
 * @param {string} html
 * @returns {Array<{ tag: string, outer: string, inner: string, textContent: string, normalizedText: string }>}
 */
function getBlocks(html) {
  if (typeof html !== "string" || !html.trim()) return [];
  const wrapped = `<div id="html-diff-root">${html}</div>`;
  const doc = new DOMParser().parseFromString(wrapped, "text/html");
  const root = doc.getElementById("html-diff-root");
  if (!root) return [];
  
  // Get all block nodes
  const allNodes = Array.from(root.querySelectorAll(BLOCK_SELECTOR));
  
  // Filter for top-level blocks only (exclude nested blocks like <p> inside <li> inside <ol>)
  const topLevelNodes = allNodes.filter(
    (el) => !hasBlockAncestorInList(el, allNodes)
  );
  
  const blocks = [];
  topLevelNodes.forEach((el) => {
    const tag = el.tagName.toLowerCase();
    const inner = el.innerHTML;
    const outer = el.outerHTML;
    
    // Extract text content (handles React wrappers)
    const textContent = extractTextContent(inner);
    const normalizedText = normalizeTextForComparison(textContent);
    const matchText = normalizeTextForMatching(textContent); // Aggressively normalized for matching (removes all punctuation)
    
    // Include all blocks (including empty ones) so they can be matched correctly
    blocks.push({ tag, outer, inner, textContent, normalizedText, matchText });
  });
  return blocks;
}

/**
 * Add or merge class on an opening tag string (first occurrence of tag).
 * @param {string} outerHtml - e.g. "<p>foo</p>"
 * @param {string} className - e.g. "ai-modified"
 * @returns {string}
 */
function addClassToBlockHtml(outerHtml, className) {
  if (!outerHtml || !className) return outerHtml;
  const hasClass = /class\s*=\s*["'][^"']*["']/i.test(outerHtml);
  if (hasClass) {
    return outerHtml.replace(
      /class\s*=\s*["']([^"']*)["']/i,
      (_, existing) => {
        const combined = (existing + " " + className).replace(/\s+/g, " ").trim();
        const deduped = [...new Set(combined.split(" ").filter(Boolean))].join(" ");
        return `class="${deduped}"`;
      }
    );
  }
  const openTag = outerHtml.match(/^<(\w+)(\s[^>]*)?>/i);
  if (!openTag) return outerHtml;
  const tagName = openTag[1];
  const rest = openTag[2] || "";
  const newOpen = `<${tagName}${rest ? rest + " " : " "}class="${className}">`;
  return outerHtml.replace(/^<\w+[^>]*>/i, newOpen);
}

/**
 * Wrap block inner HTML with an inline highlight span (text-only highlight).
 * This avoids painting the full-width block background (which highlights empty space on wrapped lines).
 */
function addInlineHighlightToBlockHtml(outerHtml, className) {
  if (!outerHtml || !className) return outerHtml;

  try {
    const doc = new DOMParser().parseFromString(outerHtml, "text/html");
    const el = doc.body?.firstElementChild;
    if (!el) return outerHtml;

    const inner = el.innerHTML || "";
    const text = extractTextContent(inner);
    if (!text || !String(text).trim()) return el.outerHTML || outerHtml;

    // Avoid double-wrapping.
    const first = el.firstElementChild;
    if (first?.tagName?.toLowerCase() === "span") {
      const cls = String(first.getAttribute("class") || "");
      if (cls.includes("ai-inline") && cls.includes(className)) {
        return el.outerHTML || outerHtml;
      }
    }

    const span = doc.createElement("span");
    span.setAttribute("class", `ai-inline ${className}`);
    span.innerHTML = inner;
    el.innerHTML = "";
    el.appendChild(span);

    return el.outerHTML || outerHtml;
  } catch (_) {
    return outerHtml;
  }
}

function addClassToListItemHtml(listOuterHtml, className) {
  if (!listOuterHtml || !className) return listOuterHtml;
  try {
    const doc = new DOMParser().parseFromString(listOuterHtml, "text/html");
    const list = doc.body?.firstElementChild;
    if (!list) return listOuterHtml;
    list.querySelectorAll("li").forEach((li) => {
      const parts = (li.getAttribute("class") || "").split(/\s+/).filter(Boolean);
      if (!parts.includes(className)) parts.push(className);
      li.setAttribute("class", parts.join(" "));
    });
    return list.outerHTML || listOuterHtml;
  } catch (_) {
    return listOuterHtml;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-item list diff helpers
//
// The block-level diff treats an entire <ol>/<ul> as one unit. That's wrong
// for lists: changing a single sub-item should highlight only THAT item, not
// every sibling and nested cousin. The helpers below let `diffHtml` walk a
// matched pair of lists and apply ai-inline only on the items whose direct
// text actually changed.
// ─────────────────────────────────────────────────────────────────────────────

function getDirectListItems(listEl) {
  return Array.from(listEl.children).filter(
    (c) => c.tagName?.toLowerCase() === "li",
  );
}

function getDirectNestedLists(li) {
  return Array.from(li.children).filter((c) => {
    const t = c.tagName?.toLowerCase();
    return t === "ol" || t === "ul";
  });
}

// Match key for a single <li>: aggressively normalized text of THIS <li>
// only, with all nested <ol>/<ul> contents stripped so a sub-list change
// doesn't change the parent's identity.
function liDirectMatchKey(li) {
  const clone = li.cloneNode(true);
  clone.querySelectorAll("ol, ul").forEach((n) => n.remove());
  const text = extractTextContent(clone.innerHTML || "");
  return normalizeTextForMatching(text);
}

// Wrap inline content of <li> (or its direct <p>/heading children) with an
// ai-inline span. Nested <ol>/<ul> are left untouched so per-item diffing
// can still recurse into them.
function wrapLiInline(li, doc, className) {
  const alreadyWrapped = (container) => {
    const first = container.firstElementChild;
    if (first?.tagName?.toLowerCase() !== "span") return false;
    const cls = String(first.getAttribute("class") || "");
    return cls.includes("ai-inline") && cls.includes(className);
  };

  const wrapInner = (container) => {
    if (!container) return;
    const inner = container.innerHTML || "";
    const text = extractTextContent(inner);
    if (!text || !String(text).trim()) return;
    if (alreadyWrapped(container)) return;
    const span = doc.createElement("span");
    span.setAttribute("class", `ai-inline ${className}`);
    span.innerHTML = inner;
    container.innerHTML = "";
    container.appendChild(span);
  };

  // Preferred: wrap each direct <p>/heading child (most common Tiptap output).
  const directInlineHosts = Array.from(li.children).filter((child) => {
    const tag = child.tagName?.toLowerCase();
    return tag === "p" || /^h[1-6]$/.test(tag);
  });
  if (directInlineHosts.length > 0) {
    directInlineHosts.forEach((host) => wrapInner(host));
    return;
  }

  const children = Array.from(li.children);
  const onlyNestedLists =
    children.length > 0 &&
    children.every((c) => {
      const t = c.tagName?.toLowerCase();
      return t === "ol" || t === "ul";
    });
  if (onlyNestedLists) return; // nothing inline to highlight on this <li>

  // Detach nested lists, wrap the remaining inline content, then reattach
  // them so the span never contains a block-level <ol>/<ul>.
  const nestedLists = children.filter((c) => {
    const t = c.tagName?.toLowerCase();
    return t === "ol" || t === "ul";
  });
  if (nestedLists.length === 0) {
    wrapInner(li);
    return;
  }
  nestedLists.forEach((n) => n.remove());
  wrapInner(li);
  nestedLists.forEach((n) => li.appendChild(n));
}

function markAllListItemsRecursive(listEl, doc, className) {
  getDirectListItems(listEl).forEach((li) => {
    wrapLiInline(li, doc, className);
    getDirectNestedLists(li).forEach((nested) => {
      markAllListItemsRecursive(nested, doc, className);
    });
  });
}

// Pair nested <ol>/<ul> children of two matched <li>s by tag (ol↔ol, ul↔ul)
// and recursively diff each pair. Result-side lists with no original
// counterpart are treated as wholly added.
function pairAndDiffNestedLists(origNested, resNested, doc) {
  const used = new Set();
  resNested.forEach((rNested) => {
    let matchIdx = -1;
    for (let i = 0; i < origNested.length; i++) {
      if (used.has(i)) continue;
      if (origNested[i].tagName === rNested.tagName) {
        matchIdx = i;
        break;
      }
    }
    if (matchIdx >= 0) {
      used.add(matchIdx);
      diffListInPlace(origNested[matchIdx], rNested, doc);
    } else {
      markAllListItemsRecursive(rNested, doc, AI_DIFF_HIGHLIGHT_CLASS);
    }
  });
}

// Per-item diff between an original and result list element. Mutates the
// result list element in place so that:
//   - Items whose direct text matches an original sibling get NO highlight
//     (we still recurse into their nested lists).
//   - Items whose direct text changed are wrapped with ai-added.
//   - Items with no positional counterpart are wrapped with ai-added.
function diffListInPlace(origList, resList, doc) {
  const origLis = getDirectListItems(origList);
  const resLis = getDirectListItems(resList);
  const origKeys = origLis.map(liDirectMatchKey);
  const usedOrig = new Set();

  resLis.forEach((resLi) => {
    const resKey = liDirectMatchKey(resLi);

    // Step 1: prefer an exact direct-text match (item is unchanged).
    let matchIdx = -1;
    if (resKey.length > 0) {
      for (let i = 0; i < origLis.length; i++) {
        if (usedOrig.has(i)) continue;
        if (origKeys[i] === resKey) {
          matchIdx = i;
          break;
        }
      }
    }

    if (matchIdx >= 0) {
      usedOrig.add(matchIdx);
      pairAndDiffNestedLists(
        getDirectNestedLists(origLis[matchIdx]),
        getDirectNestedLists(resLi),
        doc,
      );
      return;
    }

    // Step 2: pair with the next unused original <li> as a changed item.
    let modifiedIdx = -1;
    for (let i = 0; i < origLis.length; i++) {
      if (!usedOrig.has(i)) {
        modifiedIdx = i;
        break;
      }
    }

    if (modifiedIdx >= 0) {
      usedOrig.add(modifiedIdx);
      wrapLiInline(resLi, doc, AI_DIFF_HIGHLIGHT_CLASS);
      pairAndDiffNestedLists(
        getDirectNestedLists(origLis[modifiedIdx]),
        getDirectNestedLists(resLi),
        doc,
      );
    } else {
      // Step 3: truly new — this <li> and any nested items are added.
      wrapLiInline(resLi, doc, AI_DIFF_HIGHLIGHT_CLASS);
      getDirectNestedLists(resLi).forEach((nested) => {
        markAllListItemsRecursive(nested, doc, AI_DIFF_HIGHLIGHT_CLASS);
      });
    }
  });
}

// Public entry: run a per-item diff between two list outerHTMLs and return
// the result list HTML with ai-inline spans only on the items that changed.
function diffListsHtml(origListOuter, resultListOuter) {
  if (!resultListOuter) return resultListOuter;
  if (!origListOuter) return resultListOuter;
  try {
    const origDoc = new DOMParser().parseFromString(origListOuter, "text/html");
    const resDoc = new DOMParser().parseFromString(resultListOuter, "text/html");
    const origList = origDoc.body?.firstElementChild;
    const resList = resDoc.body?.firstElementChild;
    if (!origList || !resList) return resultListOuter;
    if (origList.tagName !== resList.tagName) {
      // Different list type (ol vs ul) — fall back to whole-list highlight.
      return addInlineHighlightToListItemsHtml(
        resultListOuter,
        AI_DIFF_HIGHLIGHT_CLASS,
      );
    }
    diffListInPlace(origList, resList, resDoc);
    return resList.outerHTML || resultListOuter;
  } catch (_) {
    return resultListOuter;
  }
}

function addInlineHighlightToListItemsHtml(listOuterHtml, className) {
  if (!listOuterHtml || !className) return listOuterHtml;
  try {
    const doc = new DOMParser().parseFromString(listOuterHtml, "text/html");
    const list = doc.body?.firstElementChild;
    if (!list) return listOuterHtml;

    const alreadyWrapped = (container) => {
      const first = container.firstElementChild;
      if (first?.tagName?.toLowerCase() !== "span") return false;
      const cls = String(first.getAttribute("class") || "");
      return cls.includes("ai-inline") && cls.includes(className);
    };

    const wrapInlineContent = (container) => {
      if (!container) return;
      const inner = container.innerHTML || "";
      const text = extractTextContent(inner);
      if (!text || !String(text).trim()) return;
      if (alreadyWrapped(container)) return;

      const span = doc.createElement("span");
      span.setAttribute("class", `ai-inline ${className}`);
      span.innerHTML = inner;
      container.innerHTML = "";
      container.appendChild(span);
    };

    // querySelectorAll("li") returns BOTH outer and nested <li> nodes; each is
    // processed independently so nested items get their own ai-inline span.
    list.querySelectorAll("li").forEach((li) => {
      // Preferred path: wrap inline content of each direct <p>/heading child.
      // Tiptap normally renders list items as <li><p>text</p></li>, and the
      // AIDiffInline mark only applies to inline content — wrapping the <p>
      // (instead of the whole <li>) keeps the HTML valid (no block-in-inline)
      // so ProseMirror preserves the highlight when setContent re-parses it.
      const directInlineHosts = Array.from(li.children).filter((child) => {
        const tag = child.tagName?.toLowerCase();
        return tag === "p" || /^h[1-6]$/.test(tag);
      });

      if (directInlineHosts.length > 0) {
        directInlineHosts.forEach((host) => wrapInlineContent(host));
        return;
      }

      // No paragraph wrapper — if the <li> only contains nested <ol>/<ul>,
      // skip it (those nested <li>s will be wrapped by this same loop).
      // Otherwise wrap the <li>'s direct inline text.
      const children = Array.from(li.children);
      const onlyNestedLists =
        children.length > 0 &&
        children.every((child) => {
          const tag = child.tagName?.toLowerCase();
          return tag === "ol" || tag === "ul";
        });

      if (!onlyNestedLists) wrapInlineContent(li);
    });

    return list.outerHTML || listOuterHtml;
  } catch (_) {
    return listOuterHtml;
  }
}

/**
 * Build a map of normalized text content -> array of block indices in original.
 * Uses aggressively normalized text content (matchText) for matching, which handles
 * structural differences and punctuation variations between TipTap's React NodeViews and plain HTML.
 * For lists, uses extracted list item text content.
 * @param {Array<{matchText: string, normalizedText: string, tag: string, inner: string}>} blocks
 * @returns {Map<string, number[]>}
 */
function blockIndexByContent(blocks) {
  const map = new Map();
  blocks.forEach((b, i) => {
    let key;
    
    // Special handling for lists: extract list item text content and normalize aggressively
    if (b.tag === "ol" || b.tag === "ul") {
      const listText = extractListTextContent(b.inner);
      key = listText ? normalizeTextForMatching(listText) : `__empty__${b.tag}`;
    } else {
      // Primary key: aggressively normalized text content (removes all punctuation for flexible matching)
      const textKey = b.matchText || "";
      // For empty blocks, use tag-based key
      key = textKey || `__empty__${b.tag}`;
    }
    
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(i);
  });
  return map;
}

/**
 * Compare originalHtml and resultHtml; return result HTML with .ai-added
 * applied only where changes exist.
 * Uses text content comparison to handle structural differences (e.g., React NodeViews).
 * Properly handles Hebrew/RTL text and empty blocks.
 * @param {string} originalHtml
 * @param {string} resultHtml
 * @returns {string}
 */
export function diffHtml(originalHtml, resultHtml) {
  try {
    // Important: ignore any previous AI diff markup in the current editor HTML,
    // otherwise the second Process AI click can incorrectly re-mark the same paragraphs as "added".
    const orig = unwrapSingleRootDiv(stripPreviousAiDiffMarkup(originalHtml || ""));
    const result = unwrapSingleRootDiv(resultHtml || "");
    const origBlocks = getBlocks(orig);
    const resultBlocks = getBlocks(result);

    const origByContent = blockIndexByContent(origBlocks);
    const usedOrig = new Set();

    const resultWithClasses = resultBlocks.map((resBlock) => {
      // Check if this is an empty block
      const isResEmpty = isEmptyBlock(resBlock.inner, resBlock.textContent);
      
      // Special handling for lists: use list item text content (aggressively normalized)
      let matchKey;
      if (resBlock.tag === "ol" || resBlock.tag === "ul") {
        const listText = extractListTextContent(resBlock.inner);
        matchKey = listText ? normalizeTextForMatching(listText) : `__empty__${resBlock.tag}`;
      } else if (isResEmpty) {
        matchKey = `__empty__${resBlock.tag}`;
      } else {
        matchKey = resBlock.matchText || ""; // Use aggressively normalized text for matching
      }
      
      // Try to find matching block
      let origIndices = origByContent.get(matchKey) || [];
      
      // Find first unused matching block
      let usedIndex = -1;
      let bestMatch = null;
      
      for (const i of origIndices) {
        if (!usedOrig.has(i)) {
          const origBlock = origBlocks[i];
          // Match by tag type
          if (origBlock.tag === resBlock.tag) {
            // For lists, compare list item text content (aggressively normalized)
            if (resBlock.tag === "ol" || resBlock.tag === "ul") {
              const origListText = extractListTextContent(origBlock.inner);
              const resListText = extractListTextContent(resBlock.inner);
              const origListMatch = origListText ? normalizeTextForMatching(origListText) : "";
              const resListMatch = resListText ? normalizeTextForMatching(resListText) : "";
              if (origListMatch === resListMatch) {
                usedOrig.add(i);
                usedIndex = i;
                bestMatch = origBlock;
                break;
              }
            }
            // For empty blocks, match by tag only
            else if (isResEmpty) {
              const isOrigEmpty = isEmptyBlock(origBlock.inner, origBlock.textContent);
              if (isOrigEmpty) {
                usedOrig.add(i);
                usedIndex = i;
                bestMatch = origBlock;
                break;
              }
            }
            // For non-empty blocks, compare aggressively normalized text (handles punctuation differences)
            else if (origBlock.matchText === resBlock.matchText && origBlock.matchText.length > 0) {
              usedOrig.add(i);
              usedIndex = i;
              bestMatch = origBlock;
              break;
            }
          }
        }
      }
      
      // List positional fallback: if we couldn't match this list by joined
      // text, pair it with the next unused original list of the SAME tag
      // anyway. The per-item diff below will figure out which sub-items
      // actually changed instead of marking the whole list as added.
      if (usedIndex < 0 && (resBlock.tag === "ol" || resBlock.tag === "ul")) {
        for (let i = 0; i < origBlocks.length; i++) {
          if (!usedOrig.has(i) && origBlocks[i].tag === resBlock.tag) {
            usedOrig.add(i);
            usedIndex = i;
            bestMatch = origBlocks[i];
            break;
          }
        }
      }

      // If no match found by key, try direct comparison with unused blocks
      // This handles edge cases where normalization might differ slightly
      if (usedIndex < 0) {
        if (isResEmpty && resBlock.tag !== "ol" && resBlock.tag !== "ul") {
          // For empty blocks, try to match with any unused empty block of the same tag
          // This handles cases where response has more empty blocks than original
          for (let i = 0; i < origBlocks.length; i++) {
            if (!usedOrig.has(i)) {
              const origBlock = origBlocks[i];
              const isOrigEmpty = isEmptyBlock(origBlock.inner, origBlock.textContent);
              if (isOrigEmpty && origBlock.tag === resBlock.tag) {
                usedOrig.add(i);
                usedIndex = i;
                bestMatch = origBlock;
                break;
              }
            }
          }
        } else if (!isResEmpty && resBlock.tag !== "ol" && resBlock.tag !== "ul") {
          // For non-empty blocks, compare aggressively normalized text content
          // This handles punctuation differences (commas, periods, etc.)
          for (let i = 0; i < origBlocks.length; i++) {
            if (!usedOrig.has(i)) {
              const origBlock = origBlocks[i];
              const isOrigEmpty = isEmptyBlock(origBlock.inner, origBlock.textContent);
              
              if (
                !isOrigEmpty &&
                origBlock.tag === resBlock.tag &&
                origBlock.matchText === resBlock.matchText &&
                origBlock.matchText.length > 0
              ) {
                usedOrig.add(i);
                usedIndex = i;
                bestMatch = origBlock;
                break;
              }
            }
          }
        }
      }
      
      // Determine if block is modified or unchanged
      if (usedIndex >= 0 && bestMatch) {
        // Block exists in original - check if content is identical
        if (resBlock.tag === "ol" || resBlock.tag === "ul") {
          // For lists, compare list item text content (aggressively normalized)
          const origListText = extractListTextContent(bestMatch.inner);
          const resListText = extractListTextContent(resBlock.inner);
          const origListMatch = origListText ? normalizeTextForMatching(origListText) : "";
          const resListMatch = resListText ? normalizeTextForMatching(resListText) : "";
          if (origListMatch === resListMatch) {
            return resBlock.outer; // Identical (ignoring punctuation differences) - no highlight
          }
          // List changed somewhere — run a per-item diff so only the actual
          // changed <li>s get an ai-inline span (not the whole list a→z).
          return diffListsHtml(bestMatch.outer, resBlock.outer);
        }
        
        const isBestMatchEmpty = isEmptyBlock(bestMatch.inner, bestMatch.textContent);
        
        if (isResEmpty && isBestMatchEmpty) {
          // Both are empty - no highlight needed
          return resBlock.outer;
        }
        
        // Compare using aggressively normalized text (matchText)
        // If matchText matches, content is semantically identical (may differ only by punctuation)
        if (bestMatch.matchText === resBlock.matchText && bestMatch.matchText.length > 0) {
          // Content is semantically identical (differs only by punctuation) - no highlight needed
          return resBlock.outer;
        } else {
          if (resBlock.tag === "table") {
            return addClassToBlockHtml(resBlock.outer, AI_DIFF_HIGHLIGHT_CLASS);
          }
          return addInlineHighlightToBlockHtml(
            resBlock.outer,
            AI_DIFF_HIGHLIGHT_CLASS,
          );
        }
      }
      
      // Block not found in original
      // For empty blocks, don't mark as added (they're just formatting)
      if (isResEmpty && resBlock.tag !== "ol" && resBlock.tag !== "ul") {
        return resBlock.outer;
      }
      
      if (resBlock.tag === "ol" || resBlock.tag === "ul") {
        const withLiClass = addClassToListItemHtml(
          resBlock.outer,
          AI_DIFF_HIGHLIGHT_CLASS,
        );
        return addInlineHighlightToListItemsHtml(
          withLiClass,
          AI_DIFF_HIGHLIGHT_CLASS,
        );
      }
      if (resBlock.tag === "table") {
        return addClassToBlockHtml(resBlock.outer, AI_DIFF_HIGHLIGHT_CLASS);
      }
      return addInlineHighlightToBlockHtml(
        resBlock.outer,
        AI_DIFF_HIGHLIGHT_CLASS,
      );
    });

    if (resultWithClasses.length === 0) return result;
    return resultWithClasses.join("");
  } catch (_) {
    return typeof resultHtml === "string" ? resultHtml : "";
  }
}
